import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled from 'styled-components';

import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import { useConfiguration, useI18n } from '../../hooks';
import NoValue from '../NoValue/NoValue';
import cap from '../../utils/cap';

import type { DateTimeFormat, DateTimeVariant } from './DateTime.types';
import { parseToDate } from './Input/utils';
import type { ClockFormat } from './Input/utils';
import { isValidDate } from './Picker/utils';
import { formatDateTime } from './utils';

export interface DateTimeDisplayProps extends BaseProps {
  /** Date as ISO8601 string, timestamp or native Date object. */
  value?: Date | string | number;
  /** Controls date time variant. */
  variant: DateTimeVariant;
  /**
   * Controls display format.
   * @default 'medium'
   */
  format?: DateTimeFormat;
  /** Defines clock format that overrides locale based format */
  clockFormat?: ClockFormat;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLSpanElement>;
}

export const StyledDateTimeDisplay = styled.span``;

const DateTimeDisplay: ForwardRefForwardPropsComponent<DateTimeDisplayProps> = forwardRef(
  function DateTimeDisplay(
    {
      value,
      variant,
      format = 'medium',
      clockFormat,
      ...restProps
    }: PropsWithoutRef<DateTimeDisplayProps>,
    ref: DateTimeDisplayProps['ref']
  ) {
    const { locale } = useConfiguration();
    const t = useI18n();

    return (
      <StyledDateTimeDisplay ref={ref} {...restProps}>
        {(value || value === 0) && isValidDate(parseToDate(value)) ? (
          cap(
            formatDateTime(parseToDate(value), {
              t,
              locale,
              format,
              variant,
              clockFormat,
              timeZone: 'UTC'
            })
          )
        ) : (
          <NoValue />
        )}
      </StyledDateTimeDisplay>
    );
  }
);

export default DateTimeDisplay;
